import { useLazyQuery } from '@apollo/client';
import {
  fetchCampaignByIdQuery,
  fetchCampaignLabels,
  fetchMICampaignsQuery,
  fetchMIMessageTypesQuery,
  fetchMIWorkflowsQuery,
  fetchWorkflowQuery,
} from 'api/queries';
import { omit } from 'lodash';
import debounce from 'lodash/debounce';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiFilterSelect, MultiSelect } from 'ui/selects';
import { spacing } from 'ui/theme';

import { TemplateType } from 'constants/templateTypes';
import {
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
  selectIsUserContextLoaded,
} from 'core/userContext/selectors';
import { FinishedBadge, RecurringBadge, RunningBadge } from 'ui/badge';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import {
  CampaignTypeCategory,
  CampaignTypeCategoryLabels,
  CampaignTypeOptions,
  NiceNameTypes,
  NiceTemplateTypes,
  TemplateTypeOptions,
} from '../constants';
import { useCampaignAndMetricFiltersContext } from '../context';
import { SelectDropdownInfoText } from '../Styled';
import {
  CampaignFilters,
  CampaignFiltersKeys,
  CampaignStates,
  CampaignTypes,
  GqlFetchCampaignLabelsResponse,
  GqlMICampaignsQueryResponse,
  GqlMIMessageTypesQueryResponse,
  GqlMIWorkflowsQueryResponse,
  CampaignFilterOption as Option,
} from '../types';
import { FilterList } from './FilterList';

type NameById = { [id: string]: string };

type ChannelLabels = { [channel: string]: string };

const FETCH_POLICY = 'cache-and-network';

const parseFilterSelect = (
  key: CampaignFiltersKeys,
  val: (CampaignStates | CampaignTypes | TemplateType | number)[],
  filterOpts: CampaignFilters,
  setFilterOpts: Dispatch<SetStateAction<CampaignFilters>>,
) => {
  if (val?.length === 0) {
    setFilterOpts(omit(filterOpts, key));
  } else {
    setFilterOpts({ ...filterOpts, [key]: val });
  }
};

const resultsCheck = (results: Option[], id: number) => {
  const resultIds = Object.values(results).map((el) => el.id);
  return resultIds.includes(id as unknown as string);
};

export const FilterBuilder = () => {
  const isUserContextLoaded = useSelector(selectIsUserContextLoaded);
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const {
    selectedMediums: mediums,
    campaignFilters,
    setCampaignFilters,
  } = useCampaignAndMetricFiltersContext();

  const [campaignResults, setCampaignResults] = useState<Option[]>([]);
  const [workflowResults, setWorkflowResults] = useState<Option[]>([]);
  const [messageTypeResults, setMessageTypeResults] = useState<Option[]>([]);
  const [messageChannels, setMessageChannels] = useState<ChannelLabels>({});
  const [campaignLabels, setCampaignLabels] = useState<Option[]>([]);
  const [additionalCampaigns, setAdditionalCampaigns] = useState<Option[]>([]);
  const [additionalWorkflows, setAdditionalWorkflows] = useState<Option[]>([]);
  const [niceIds, setNiceIds] = useState({});
  const [niceWorkflowIds, setNiceWorkflowIds] = useState({});
  const [niceLabels, setNiceLabels] = useState({});
  const [niceMessageTypes, setNiceMessageTypes] = useState({});

  const options = [
    { label: 'State', id: CampaignFiltersKeys.STATES },
    { label: 'Type', id: CampaignFiltersKeys.TYPES },
    { label: 'Name / ID', id: CampaignFiltersKeys.CAMPAIGN_IDS },
    { label: 'Label', id: CampaignFiltersKeys.LABEL_IDS },
    { label: 'Associated journey', id: CampaignFiltersKeys.WORKFLOW_IDS },
    { label: 'Message channel / type', id: CampaignFiltersKeys.MESSAGE_TYPE_IDS },
    ...(isWhatsAppFFOn ? [{ label: 'Template type', id: CampaignFiltersKeys.TEMPLATE_TYPES }] : []),
  ];

  const keyNiceNames = {
    states: 'State',
    types: 'Type',
    campaignIds: 'Name / ID',
    workflowIds: 'Journey',
    labelIds: 'Label',
    messageTypeIds: 'Message channel / type',
    templateTypes: 'Template type',
  };

  const campaignTypeOptions = [
    ...CampaignTypeOptions,
    ...(isEmbeddedMessagingEnabled
      ? [
          {
            categoryId: CampaignTypeCategory.EMBEDDED,
            label: 'Eligibility',
            id: CampaignTypes.ELIGIBILITY,
            value: CampaignTypes.ELIGIBILITY,
          },
        ]
      : []),
  ];

  const [getCampaign] = useLazyQuery(fetchCampaignByIdQuery, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp) => {
      const result = resp?.campaign;
      if (result) {
        setAdditionalCampaigns([
          ...additionalCampaigns,
          {
            label: result.name,
            id: result.id,
            value: result.id,
            doubleLine: `ID ${result.id} | ${result.campaignState} | ${result.campaignTypeDefinition}`,
          },
        ]);
        setNiceIds({
          ...niceIds,
          [result.id]: result.name,
        });
      }
    },
  });
  const [getWorkflow] = useLazyQuery(fetchWorkflowQuery, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp) => {
      const result = resp?.workflow;
      if (result) {
        setAdditionalWorkflows([
          ...additionalWorkflows,
          {
            label: result.name,
            id: result.id,
            value: result.id,
          },
        ]);
        setNiceWorkflowIds({
          ...niceWorkflowIds,
          [result.id]: result.name,
        });
      }
    },
  });

  const [getItems] = useLazyQuery(fetchMICampaignsQuery, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp: GqlMICampaignsQueryResponse) => {
      const results = resp?.campaigns?.results || [];
      const campaignObj: NameById = {};
      setCampaignResults(
        results.map((camp) => {
          campaignObj[camp.id] = camp.name;
          return {
            label: camp.name,
            id: camp.id as unknown as string,
            value: camp.id,
            doubleLine: `ID ${camp.id} | ${camp.campaignState} | ${camp.campaignTypeDefinition}`,
          };
        }),
      );
      setNiceIds({
        ...niceIds,
        ...campaignObj,
      });
    },
  });

  const [getWFs] = useLazyQuery(fetchMIWorkflowsQuery, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp: GqlMIWorkflowsQueryResponse) => {
      const results = resp?.workflows?.results || [];
      const workflowObj: NameById = {};
      setWorkflowResults(
        results.map((label) => {
          workflowObj[label.id] = label.name;
          return {
            label: label.name,
            id: label.id as unknown as string,
            value: label.id,
          };
        }),
      );
      setNiceWorkflowIds({
        ...niceWorkflowIds,
        ...workflowObj,
      });
    },
  });

  const [getMessageTypes] = useLazyQuery(fetchMIMessageTypesQuery, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp: GqlMIMessageTypesQueryResponse) => {
      const results = resp?.messageTypes || [];
      const categoryLabels: ChannelLabels = {};
      const messageTypeObj: NameById = {};
      setMessageTypeResults(
        results.map((msgType) => {
          const msgChannel = msgType?.messageChannel?.name;
          messageTypeObj[msgType.id] = msgType.name;
          if (!categoryLabels[msgChannel]) {
            categoryLabels[msgChannel] = msgChannel;
          }
          return {
            categoryId: msgChannel,
            label: msgType.name,
            id: msgType.id as unknown as string,
            value: msgType.id,
          };
        }),
      );
      setNiceMessageTypes({
        ...niceMessageTypes,
        ...messageTypeObj,
      });
      setMessageChannels(categoryLabels);
    },
  });

  const [getLabels] = useLazyQuery(fetchCampaignLabels, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (resp: GqlFetchCampaignLabelsResponse) => {
      const results = resp?.campaignLabels || [];
      const labelObj: NameById = {};
      setCampaignLabels(
        results.map((label) => {
          labelObj[label.id] = label.name;
          return {
            label: label.name,
            id: label.id as unknown as string,
            value: label.id,
          };
        }),
      );
      setNiceLabels(labelObj);
    },
  });

  useEffect(() => {
    setAdditionalCampaigns([]);
    campaignFilters?.campaignIds?.forEach((id) => {
      if (!resultsCheck(campaignResults, id)) {
        getCampaign({ variables: { id } });
      }
    });
  }, [campaignResults]);

  useEffect(() => {
    setAdditionalWorkflows([]);
    campaignFilters?.workflowIds?.forEach((id) => {
      if (!resultsCheck(workflowResults, id)) {
        getWorkflow({ variables: { id } });
      }
    });
  }, [workflowResults]);

  useEffect(() => {
    getWFs();
    getLabels();
  }, []);

  useEffect(() => {
    if (isUserContextLoaded && mediums?.length > 0) {
      getItems({
        variables: {
          campaignFilterInfo: {
            messageMedium: mediums,
            campaignState: ['Aborted', 'Finished', 'Recurring', 'Running', 'Scheduled'],
          },
          pagination: { limit: 500, offset: 0 },
        },
      });
      getMessageTypes();
    }
  }, [mediums]);

  const handleFilterClose = (id: string | null | undefined) => {
    switch (id) {
      case 'campaignIds':
        if (isUserContextLoaded) {
          getItems({
            variables: {
              campaignFilterInfo: {
                messageMedium: mediums,
                campaignState: ['Aborted', 'Finished', 'Recurring', 'Running', 'Scheduled'],
              },
              pagination: { limit: 500, offset: 0 },
            },
          });
        }
        break;
      case 'workflowIds':
        getWFs();
        break;
      default:
        break;
    }
  };

  const handleSearchChange = (searchQuery?: string) => {
    if (isUserContextLoaded) {
      getItems({
        variables: {
          campaignFilterInfo: { messageMedium: mediums },
          search: searchQuery,
          pagination: { limit: 500, offset: 0 },
        },
      });
    }
  };

  const handleWFSearch = (searchQuery?: string) => getWFs({ variables: { search: searchQuery } });
  return (
    <div>
      <FilterList
        setFilterOptions={setCampaignFilters}
        filterOptions={campaignFilters}
        options={options}
        keyNiceNames={keyNiceNames}
        valueNiceNames={{
          campaignIds: niceIds,
          workflowIds: niceWorkflowIds,
          messageTypeIds: niceMessageTypes,
          labelIds: niceLabels,
          types: NiceNameTypes,
          templateTypes: NiceTemplateTypes,
        }}
        onFilterClose={handleFilterClose}
      >
        <MultiSelect
          id="states"
          isEmbedded
          isFullWidth
          onSelect={(states) =>
            parseFilterSelect(
              CampaignFiltersKeys.STATES,
              states,
              campaignFilters,
              setCampaignFilters,
            )
          }
          defaultValues={campaignFilters?.states}
          options={[
            { label: <RunningBadge />, id: CampaignStates.RUNNING, value: CampaignStates.RUNNING },
            {
              label: <RecurringBadge>Aborted</RecurringBadge>,
              id: CampaignStates.ABORTED,
              value: CampaignStates.ABORTED,
            },
            {
              label: <FinishedBadge />,
              id: CampaignStates.FINISHED,
              value: CampaignStates.FINISHED,
            },
          ]}
        />
        <MultiSelect
          id="types"
          isEmbedded
          isFullWidth
          onSelect={(types) =>
            parseFilterSelect(CampaignFiltersKeys.TYPES, types, campaignFilters, setCampaignFilters)
          }
          defaultValues={campaignFilters?.types}
          options={campaignTypeOptions}
          categoryLabels={CampaignTypeCategoryLabels}
          /**
           * Sets the maximum to Number.MAX_SAFE_INTEGER to effectively disable the select all/none toggle,
           * as it assumes an infinite limit for selectable items, making a "select all" action unnecessary.
           */
          max={Number.MAX_SAFE_INTEGER}
        />
        <MultiSelect
          id="templateTypes"
          isEmbedded
          isFullWidth
          onSelect={(templateTypes) =>
            parseFilterSelect(
              CampaignFiltersKeys.TEMPLATE_TYPES,
              templateTypes,
              campaignFilters,
              setCampaignFilters,
            )
          }
          defaultValues={campaignFilters?.templateTypes}
          options={TemplateTypeOptions}
        />
        <MultiFilterSelect
          id="campaignIds"
          isEmbedded
          isFullWidth
          inputContent={{ filterPlaceholder: 'Search campaign name or ID' }}
          onSearchChange={debounce(handleSearchChange, 300)}
          onSelect={(ids) =>
            parseFilterSelect(
              CampaignFiltersKeys.CAMPAIGN_IDS,
              ids,
              campaignFilters,
              setCampaignFilters,
            )
          }
          disableInternalFiltering
          defaultValues={campaignFilters?.campaignIds}
          options={[...additionalCampaigns, ...campaignResults]}
          optionWrapperWidth={spacing.x24}
          hasSelectAllOption
        />
        <MultiFilterSelect
          id="workflowIds"
          isEmbedded
          isFullWidth
          inputContent={{ filterPlaceholder: 'Search journeys' }}
          onSearchChange={debounce(handleWFSearch, 300)}
          onSelect={(workflowIds) =>
            parseFilterSelect(
              CampaignFiltersKeys.WORKFLOW_IDS,
              workflowIds,
              campaignFilters,
              setCampaignFilters,
            )
          }
          disableInternalFiltering
          defaultValues={campaignFilters?.workflowIds}
          options={[...additionalWorkflows, ...workflowResults]}
          optionWrapperWidth={spacing.x24}
          hasSelectAllOption
        />
        {campaignLabels?.length > 0 ? (
          <MultiFilterSelect
            id="labelIds"
            isEmbedded
            isFullWidth
            inputContent={{ filterPlaceholder: 'Search labels' }}
            onSelect={(labelIds) =>
              parseFilterSelect(
                CampaignFiltersKeys.LABEL_IDS,
                labelIds,
                campaignFilters,
                setCampaignFilters,
              )
            }
            defaultValues={campaignFilters?.labelIds}
            options={campaignLabels}
            hasSelectAllOption
          />
        ) : (
          <SelectDropdownInfoText id="labelIds">No options available</SelectDropdownInfoText>
        )}
        <MultiFilterSelect
          id="messageTypeIds"
          categoryLabels={messageChannels}
          isEmbedded
          isFullWidth
          isExpandable
          inputContent={{ filterPlaceholder: 'Search message types' }}
          onSelect={(messageTypeIds) =>
            parseFilterSelect(
              CampaignFiltersKeys.MESSAGE_TYPE_IDS,
              messageTypeIds,
              campaignFilters,
              setCampaignFilters,
            )
          }
          defaultValues={campaignFilters?.messageTypeIds}
          options={messageTypeResults}
          optionWrapperWidth={spacing.x24}
        />
      </FilterList>
    </div>
  );
};
